import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const {FiDownload,FiCheckCircle,FiTarget,FiCalendar,FiTrendingUp,FiRefreshCw,FiBarChart,FiAward,FiCompass,FiUsers,FiStar}=FiIcons;

const CompletePlan=({formData,updateFormData,setCurrentStep})=> {
  const [isGeneratingPDF,setIsGeneratingPDF]=useState(false);
  const [activeCalendarView,setActiveCalendarView]=useState('monthly');
  const [taskCompletion,setTaskCompletion]=useState({});

  const allGoals=[
    ...formData.monthlyGoals?.map(goal=> ({text: goal,timeframe: 'Monthly'})) || [],
    ...formData.sixMonthGoals?.map(goal=> ({text: goal,timeframe: '6-Month'})) || [],
    ...formData.yearlyGoals?.map(goal=> ({text: goal,timeframe: 'Yearly'})) || []
  ];

  const toggleTaskCompletion=(goalIndex,taskIndex,timeframe,week,month)=> {
    const taskKey=`goal_${goalIndex}_task_${taskIndex}_${timeframe}_${week}_${month || ''}`;
    setTaskCompletion(prev=> ({...prev,[taskKey]: !prev[taskKey]}));
  };

  const isTaskCompleted=(goalIndex,taskIndex,timeframe,week,month)=> {
    const taskKey=`goal_${goalIndex}_task_${taskIndex}_${timeframe}_${week}_${month || ''}`;
    return taskCompletion[taskKey] || false;
  };

  const renderCalendarSchedule=()=> {
    const calendar=formData.scheduledTasks || {};

    if (activeCalendarView==='monthly') {
      const monthlyTasks=calendar.monthly || {};
      const weeks=['Week 1','Week 2','Week 3','Week 4'];

      return (
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Monthly Calendar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {weeks.map((week,index)=> {
              const weekKey=`week_${index + 1}`;
              const allWeekTasks=[];
              
              const monthlyWeekTasks=monthlyTasks[weekKey] || [];
              allWeekTasks.push(...monthlyWeekTasks);
              
              const sixMonthWeekKey=`month_1_week_${index + 1}`;
              const sixMonthTasks=calendar.sixMonth?.[sixMonthWeekKey] || [];
              allWeekTasks.push(...sixMonthTasks);
              
              const yearlyMonth1Tasks=calendar.yearly?.['quarter_1']?.filter(task=> 
                task.month===1 && task.week===index + 1
              ) || [];
              allWeekTasks.push(...yearlyMonth1Tasks);

              return (
                <div key={weekKey} className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-800 mb-3">{week}</h4>
                  <div className="space-y-2">
                    {allWeekTasks.length > 0 ? (
                      allWeekTasks.map((task,taskIndex)=> (
                        <div key={taskIndex} className="bg-white p-3 rounded border border-blue-200">
                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              checked={isTaskCompleted(
                                allGoals.findIndex(g=> g.text===task.goalText),
                                taskIndex,
                                task.timeframe,
                                task.week,
                                task.month
                              )}
                              onChange={()=> {
                                const goalIndex=allGoals.findIndex(g=> g.text===task.goalText);
                                toggleTaskCompletion(
                                  goalIndex,
                                  taskIndex,
                                  task.timeframe,
                                  task.week,
                                  task.month
                                );
                              }}
                              className="w-4 h-4 border-2 border-gray-300 rounded mt-0.5 flex-shrink-0 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className={`text-base font-medium text-gray-800 ${
                                isTaskCompleted(
                                  allGoals.findIndex(g=> g.text===task.goalText),
                                  taskIndex,
                                  task.timeframe,
                                  task.week,
                                  task.month
                                ) ? 'line-through opacity-60' : ''
                              }`}>
                                {task.text}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">{task.goalText}</div>
                              <div className="text-xs text-blue-600 font-semibold mt-1">{task.timeframe}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-400 text-sm text-center py-4">
                        No tasks scheduled
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeCalendarView==='6month') {
      const sixMonthTasks=calendar.sixMonth || {};
      const months=['Month 1','Month 2','Month 3','Month 4','Month 5','Month 6'];

      return (
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6">6-Month Calendar</h3>
          <div className="space-y-4">
            {months.map((month,monthIndex)=> {
              const monthTasks=[];
              
              for (let week=1;week <=4;week++) {
                const weekKey=`month_${monthIndex + 1}_week_${week}`;
                const sixMonthWeekTasks=sixMonthTasks[weekKey] || [];
                monthTasks.push(...sixMonthWeekTasks);
              }
              
              const monthlyWeekTasks=calendar.monthly?.[`week_${monthIndex + 1}`] || [];
              monthTasks.push(...monthlyWeekTasks);
              
              const quarter=Math.ceil((monthIndex + 1) / 3);
              const yearlyMonthTasks=calendar.yearly?.[`quarter_${quarter}`]?.filter(task=> 
                task.month===monthIndex + 1
              ) || [];
              monthTasks.push(...yearlyMonthTasks);

              return (
                <div key={month} className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                  <h4 className="font-semibold text-purple-800 mb-3">{month}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {Array.from({length: 4},(_,weekIndex)=> {
                      const weekKey=`month_${monthIndex + 1}_week_${weekIndex + 1}`;
                      const weekTasks=sixMonthTasks[weekKey] || [];
                      
                      const monthlyTasksForWeek=calendar.monthly?.[`week_${weekIndex + 1}`] || [];
                      
                      const yearlyTasksForWeek=calendar.yearly?.[`quarter_${Math.ceil((monthIndex + 1) / 3)}`]?.filter(
                        task=> task.month===monthIndex + 1 && task.week===weekIndex + 1
                      ) || [];
                      
                      const allWeekTasks=[...weekTasks,...monthlyTasksForWeek,...yearlyTasksForWeek];

                      return (
                        <div key={weekKey} className="bg-white rounded p-2 border border-purple-200">
                          <div className="text-sm font-semibold text-purple-700 mb-1">Week {weekIndex + 1}</div>
                          <div className="space-y-1">
                            {allWeekTasks.length > 0 ? (
                              allWeekTasks.map((task,taskIndex)=> (
                                <div key={taskIndex} className="text-xs bg-purple-100 p-2 rounded">
                                  <div className="flex items-start space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={isTaskCompleted(
                                        allGoals.findIndex(g=> g.text===task.goalText),
                                        taskIndex,
                                        task.timeframe,
                                        task.week,
                                        task.month
                                      )}
                                      onChange={()=> {
                                        const goalIndex=allGoals.findIndex(g=> g.text===task.goalText);
                                        toggleTaskCompletion(
                                          goalIndex,
                                          taskIndex,
                                          task.timeframe,
                                          task.week,
                                          task.month
                                        );
                                      }}
                                      className="w-3 h-3 border border-gray-300 rounded mt-0.5 flex-shrink-0 cursor-pointer"
                                    />
                                    <div className="flex-1">
                                      <div className={`font-medium text-purple-800 text-xs leading-tight ${
                                        isTaskCompleted(
                                          allGoals.findIndex(g=> g.text===task.goalText),
                                          taskIndex,
                                          task.timeframe,
                                          task.week,
                                          task.month
                                        ) ? 'line-through opacity-60' : ''
                                      }`}>
                                        {task.text}
                                      </div>
                                      <div className={`text-purple-600 text-xs mt-1 truncate ${
                                        isTaskCompleted(
                                          allGoals.findIndex(g=> g.text===task.goalText),
                                          taskIndex,
                                          task.timeframe,
                                          task.week,
                                          task.month
                                        ) ? 'line-through opacity-60' : ''
                                      }`}>
                                        {task.goalText}
                                      </div>
                                      <div className="text-xs text-blue-600 font-semibold mt-1">{task.timeframe}</div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-gray-400 text-xs text-center py-2">
                                No tasks
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeCalendarView==='yearly') {
      const yearlyTasks=calendar.yearly || {};
      const quarters=['Q1 (Jan-Mar)','Q2 (Apr-Jun)','Q3 (Jul-Sep)','Q4 (Oct-Dec)'];

      return (
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Yearly Calendar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quarters.map((quarter,quarterIndex)=> {
              const quarterKey=`quarter_${quarterIndex + 1}`;
              const quarterYearlyTasks=yearlyTasks[quarterKey] || [];
              
              const quarterMonthlyTasks=[];
              const quarterMonths=[quarterIndex * 3 + 1,quarterIndex * 3 + 2,quarterIndex * 3 + 3];
              quarterMonths.forEach(month=> {
                if (month <=4) {
                  const monthlyWeekTasks=calendar.monthly?.[`week_${month}`] || [];
                  quarterMonthlyTasks.push(...monthlyWeekTasks);
                }
              });
              
              const quarterSixMonthTasks=[];
              const quarterSixMonthMonths=quarterMonths.filter(m=> m <=6);
              quarterSixMonthMonths.forEach(month=> {
                for (let week=1;week <=4;week++) {
                  const weekKey=`month_${month}_week_${week}`;
                  const sixMonthTasks=calendar.sixMonth?.[weekKey] || [];
                  quarterSixMonthTasks.push(...sixMonthTasks);
                }
              });
              
              const allQuarterTasks=[...quarterYearlyTasks,...quarterMonthlyTasks,...quarterSixMonthTasks];

              return (
                <div key={quarterKey} className="border border-gray-200 rounded-lg p-4 bg-green-50">
                  <h4 className="font-semibold text-green-800 mb-3">{quarter}</h4>
                  <div className="space-y-2">
                    {allQuarterTasks.length > 0 ? (
                      allQuarterTasks.map((task,taskIndex)=> (
                        <div key={taskIndex} className="bg-white p-3 rounded border border-green-200">
                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              checked={isTaskCompleted(
                                allGoals.findIndex(g=> g.text===task.goalText),
                                taskIndex,
                                task.timeframe,
                                task.week,
                                task.month
                              )}
                              onChange={()=> {
                                const goalIndex=allGoals.findIndex(g=> g.text===task.goalText);
                                toggleTaskCompletion(
                                  goalIndex,
                                  taskIndex,
                                  task.timeframe,
                                  task.week,
                                  task.month
                                );
                              }}
                              className="w-4 h-4 border-2 border-gray-300 rounded mt-0.5 flex-shrink-0 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className={`text-base font-medium text-gray-800 ${
                                isTaskCompleted(
                                  allGoals.findIndex(g=> g.text===task.goalText),
                                  taskIndex,
                                  task.timeframe,
                                  task.week,
                                  task.month
                                ) ? 'line-through opacity-60' : ''
                              }`}>
                                {task.text}
                              </div>
                              <div className={`text-sm text-gray-600 mt-1 ${
                                isTaskCompleted(
                                  allGoals.findIndex(g=> g.text===task.goalText),
                                  taskIndex,
                                  task.timeframe,
                                  task.week,
                                  task.month
                                ) ? 'line-through opacity-60' : ''
                              }`}>
                                {task.goalText}
                              </div>
                              <div className="text-xs text-green-600 font-semibold mt-1">
                                {task.timeframe} • M{task.month || 1} W{task.week || 1}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-400 text-sm text-center py-4">
                        No tasks scheduled
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  const wrapText=(doc,text,maxWidth,fontSize=10,lineHeight=1.2)=> {
    doc.setFontSize(fontSize);
    const words=text.split(' ');
    const lines=[];
    let currentLine='';

    words.forEach(word=> {
      const testLine=currentLine + (currentLine ? ' ' : '') + word;
      const testWidth=doc.getTextWidth(testLine);
      
      if (testWidth <=maxWidth) {
        currentLine=testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine=word;
        } else {
          lines.push(word);
        }
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };

  const generatePDF=async ()=> {
    setIsGeneratingPDF(true);
    
    try {
      const doc=new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Colors
      const colors={
        yellow: [255,182,35],
        red: [225,36,90],
        blue: [74,140,204],
        purple: [197,77,157],
        green: [40,176,127]
      };

      // Page dimensions
      const pageWidth=doc.internal.pageSize.getWidth();
      const pageHeight=doc.internal.pageSize.getHeight();
      const margin=15;
      const contentWidth=pageWidth - 2 * margin;

      // Add background
      const addBackground=()=> {
        doc.setFillColor(255,255,255);
        doc.rect(0,0,pageWidth,pageHeight,'F');
        
        doc.setFillColor(...colors.blue);
        doc.rect(0,0,pageWidth,20,'F');
        
        doc.setFillColor(...colors.yellow);
        doc.rect(0,pageHeight - 10,pageWidth,10,'F');
      };

      // Add header
      const addHeader=(title,page)=> {
        addBackground();
        doc.setTextColor(255,255,255);
        doc.setFontSize(10);
        doc.setFont('helvetica','bold');
        doc.text('SuperTeacher',10,14);

        doc.setTextColor(255,255,255);
        doc.setFontSize(12);
        doc.text(title,pageWidth / 2,14,{align: 'center'});

        doc.setTextColor(0,0,0);
        doc.setFontSize(11);
        doc.setFont('helvetica','bold');
        doc.text(`for ${formData.name || 'Educator'}`,pageWidth / 2,28,{align: 'center'});

        doc.setFontSize(7);
        doc.setFont('helvetica','normal');
        doc.setTextColor(100,100,100);
        doc.text(new Date().toLocaleDateString(),pageWidth / 2,33,{align: 'center'});
      };

      // Add footer
      const addFooter=(page,totalPages)=> {
        doc.setFillColor(...colors.yellow);
        doc.rect(0,pageHeight - 10,pageWidth,10,'F');
        doc.setTextColor(0,0,0);
        doc.setFontSize(6);
        doc.setFont('helvetica','italic');
        doc.text('super-teacher.net',pageWidth / 2,pageHeight - 4,{align: 'center'});
        doc.setFont('helvetica','normal');
        doc.text(`Page ${page} of ${totalPages}`,pageWidth - 8,pageHeight - 4,{align: 'right'});
      };

      // Start with first page
      addHeader('Marketing Plan & Calendar',1);
      let currentPage=1;
      let yPosition=42;

      // Educator Profile
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Educator Profile',margin + 3,yPosition);
      yPosition +=8;

      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');

      const profileData=[
        ['Name:',formData.name || 'Not specified'],
        ['Subject:',formData.subject || 'Not specified'],
        ['Experience:',formData.experience || 'Not specified']
      ];

      profileData.forEach(([label,value],index)=> {
        const xPosition=margin + (index % 3) * 45;
        const yPos=yPosition + Math.floor(index / 3) * 5;
        
        doc.setFont('helvetica','bold');
        doc.text(label,xPosition,yPos);
        
        doc.setFont('helvetica','normal');
        doc.text(value,xPosition + 15,yPos);
      });

      yPosition +=10;

      // Niche Definition
      doc.setFillColor(...colors.purple);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Niche Definition',margin + 3,yPosition);
      yPosition +=8;

      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');

      const nicheLines=wrapText(doc,formData.niche || 'Not specified',contentWidth - 10,6);
      nicheLines.forEach((line,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('Marketing Plan & Calendar',currentPage);
          yPosition=42;
        }
        doc.text(line,margin + 3,yPosition);
        yPosition +=4;
      });

      yPosition +=5;

      // Goals Section
      doc.setFillColor(...colors.green);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Your Goals',margin + 3,yPosition);
      yPosition +=8;

      doc.setTextColor(0,0,0);
      doc.setFontSize(6);

      // Monthly Goals
      if (formData.monthlyGoals && formData.monthlyGoals.length > 0) {
        doc.setFont('helvetica','bold');
        doc.setFillColor(...colors.yellow);
        doc.roundedRect(margin + 3,yPosition - 2,20,3,1,1,'F');
        doc.setTextColor(0,0,0);
        doc.text('Monthly Goals:',margin + 5,yPosition);
        yPosition +=5;
        
        doc.setFont('helvetica','normal');
        
        formData.monthlyGoals.forEach((goal,index)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('Marketing Plan & Calendar',currentPage);
            yPosition=42;
          }
          
          const goalText=`${index + 1}. ${goal}`;
          const lines=wrapText(doc,goalText,contentWidth - 10,6);
          lines.forEach(line=> {
            doc.text(line,margin + 6,yPosition);
            yPosition +=3.5;
          });
          yPosition +=1.5;
        });
      }

      // 6-Month Goals
      if (formData.sixMonthGoals && formData.sixMonthGoals.length > 0) {
        if (yPosition > pageHeight - 45) {
          doc.addPage();
          currentPage++;
          addHeader('Marketing Plan & Calendar',currentPage);
          yPosition=42;
        }
        
        yPosition +=3;
        doc.setFont('helvetica','bold');
        doc.setFillColor(...colors.purple);
        doc.roundedRect(margin + 3,yPosition - 2,25,3,1,1,'F');
        doc.setTextColor(255,255,255);
        doc.text('6-Month Goals:',margin + 5,yPosition);
        yPosition +=5;
        
        doc.setTextColor(0,0,0);
        doc.setFont('helvetica','normal');
        
        formData.sixMonthGoals.forEach((goal,index)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('Marketing Plan & Calendar',currentPage);
            yPosition=42;
          }
          
          const goalText=`${index + 1}. ${goal}`;
          const lines=wrapText(doc,goalText,contentWidth - 10,6);
          lines.forEach(line=> {
            doc.text(line,margin + 6,yPosition);
            yPosition +=3.5;
          });
          yPosition +=1.5;
        });
      }

      // Yearly Goals
      if (formData.yearlyGoals && formData.yearlyGoals.length > 0) {
        if (yPosition > pageHeight - 45) {
          doc.addPage();
          currentPage++;
          addHeader('Marketing Plan & Calendar',currentPage);
          yPosition=42;
        }
        
        yPosition +=3;
        doc.setFont('helvetica','bold');
        doc.setFillColor(...colors.red);
        doc.roundedRect(margin + 3,yPosition - 2,20,3,1,1,'F');
        doc.setTextColor(255,255,255);
        doc.text('Yearly Goals:',margin + 5,yPosition);
        yPosition +=5;
        
        doc.setTextColor(0,0,0);
        doc.setFont('helvetica','normal');
        
        formData.yearlyGoals.forEach((goal,index)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('Marketing Plan & Calendar',currentPage);
            yPosition=42;
          }
          
          const goalText=`${index + 1}. ${goal}`;
          const lines=wrapText(doc,goalText,contentWidth - 10,6);
          lines.forEach(line=> {
            doc.text(line,margin + 6,yPosition);
            yPosition +=3.5;
          });
          yPosition +=1.5;
        });
      }

      // Goals & Tasks section
      doc.addPage();
      currentPage++;
      addHeader('Goals & Action Plan',currentPage);
      yPosition=42;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(9);
      doc.setFont('helvetica','bold');
      doc.text('Detailed breakdown of goals and tasks',pageWidth / 2,35,{align: 'center'});
      
      yPosition=45;
      
      allGoals.forEach((goal,goalIndex)=> {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          currentPage++;
          addHeader('Goals & Action Plan',currentPage);
          yPosition=42;
        }
        
        // Goal header
        doc.setFillColor(...colors.blue);
        doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
        doc.setTextColor(255,255,255);
        doc.setFontSize(8);
        doc.setFont('helvetica','bold');
        doc.text(`${goal.timeframe} Goal:`,margin + 3,yPosition);
        yPosition +=6;
        
        doc.setTextColor(0,0,0);
        doc.setFontSize(7);
        doc.setFont('helvetica','bold');
        const goalLines=wrapText(doc,goal.text,contentWidth - 10,7);
        goalLines.forEach((line,index)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('Goals & Action Plan',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=4;
        });
        yPosition +=3;
        
        // Tasks
        const goalKey=`goal_${goalIndex}`;
        const tasks=formData.actionPlan?.[goalKey] || [];
        if (tasks.length > 0) {
          doc.setFont('helvetica','bold');
          doc.setTextColor(0,0,0);
          doc.text('Action Tasks:',margin + 7,yPosition);
          yPosition +=4;
          
          doc.setFont('helvetica','normal');
          doc.setFontSize(6);
          
          tasks.forEach((task,taskIndex)=> {
            if (yPosition > pageHeight - 30) {
              doc.addPage();
              currentPage++;
              addHeader('Goals & Action Plan',currentPage);
              yPosition=42;
            }
            
            // Checkbox
            const isCompleted=isTaskCompleted(goalIndex,taskIndex,goal.timeframe,task.week,task.month);
            if (isCompleted) {
              doc.line(margin + 8,yPosition - 1,margin + 10,yPosition - 1);
              doc.line(margin + 8,yPosition - 2,margin + 8,yPosition);
              doc.line(margin + 10,yPosition - 2,margin + 10,yPosition);
            } else {
              doc.rect(margin + 8,yPosition - 2,2,2);
            }
            
            const taskText=typeof task==='string' ? task : task.text;
            const lines=wrapText(doc,taskText,contentWidth - 20,6);
            lines.forEach((line,lineIndex)=> {
              if (yPosition > pageHeight - 30) {
                doc.addPage();
                currentPage++;
                addHeader('Goals & Action Plan',currentPage);
                yPosition=42;
              }
              doc.text(line,margin + 13,yPosition + (lineIndex * 3));
            });
            yPosition +=lines.length * 3 + 2;
          });
        }
        yPosition +=3;
      });
      
      // SWOT Analysis section
      doc.addPage();
      currentPage++;
      addHeader('SWOT Analysis',currentPage);
      yPosition=42;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(9);
      doc.setFont('helvetica','bold');
      doc.text('Strengths,Weaknesses,Opportunities & Threats',pageWidth / 2,35,{align: 'center'});
      
      yPosition=45;
      
      // Strengths
      doc.setFillColor(...colors.green);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Strengths',margin + 3,yPosition);
      yPosition +=6;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');
      
      const strengths=formData.marketingAnalysis?.strengths || [];
      strengths.forEach((strength,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('SWOT Analysis',currentPage);
          yPosition=42;
        }
        
        const lines=wrapText(doc,strength,contentWidth - 10,6);
        lines.forEach((line,lineIndex)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('SWOT Analysis',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=3.5;
        });
        yPosition +=1;
      });
      
      yPosition +=3;
      
      // Weaknesses
      doc.setFillColor(...colors.red);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Weaknesses',margin + 3,yPosition);
      yPosition +=6;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');
      
      const weaknesses=formData.marketingAnalysis?.weaknesses || [];
      weaknesses.forEach((weakness,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('SWOT Analysis',currentPage);
          yPosition=42;
        }
        
        const lines=wrapText(doc,weakness,contentWidth - 10,6);
        lines.forEach((line,lineIndex)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('SWOT Analysis',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=3.5;
        });
        yPosition +=1;
      });
      
      yPosition +=3;
      
      // Opportunities
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Opportunities',margin + 3,yPosition);
      yPosition +=6;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');
      
      const opportunities=formData.marketingAnalysis?.opportunities || [];
      opportunities.forEach((opportunity,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('SWOT Analysis',currentPage);
          yPosition=42;
        }
        
        const lines=wrapText(doc,opportunity,contentWidth - 10,6);
        lines.forEach((line,lineIndex)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('SWOT Analysis',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=3.5;
        });
        yPosition +=1;
      });
      
      yPosition +=3;
      
      // Threats
      doc.setFillColor(255,165,0); // Orange
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Threats',margin + 3,yPosition);
      yPosition +=6;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');
      
      const threats=formData.marketingAnalysis?.threats || [];
      threats.forEach((threat,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('SWOT Analysis',currentPage);
          yPosition=42;
        }
        
        const lines=wrapText(doc,threat,contentWidth - 10,6);
        lines.forEach((line,lineIndex)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('SWOT Analysis',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=3.5;
        });
        yPosition +=1;
      });
      
      // Recommendations section
      doc.addPage();
      currentPage++;
      addHeader('Personalized Recommendations',currentPage);
      yPosition=42;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(9);
      doc.setFont('helvetica','bold');
      doc.text('Actionable Steps for Your Success',pageWidth / 2,35,{align: 'center'});
      
      yPosition=45;
      
      doc.setFillColor(...colors.purple);
      doc.roundedRect(margin,yPosition - 3,contentWidth,4,1,1,'F');
      doc.setTextColor(255,255,255);
      doc.setFontSize(8);
      doc.setFont('helvetica','bold');
      doc.text('Recommendations',margin + 3,yPosition);
      yPosition +=6;
      
      doc.setTextColor(0,0,0);
      doc.setFontSize(6);
      doc.setFont('helvetica','normal');
      
      const recommendations=formData.marketingAnalysis?.recommendations || [];
      recommendations.forEach((recommendation,index)=> {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          currentPage++;
          addHeader('Personalized Recommendations',currentPage);
          yPosition=42;
        }
        
        const lines=wrapText(doc,recommendation,contentWidth - 10,6);
        lines.forEach((line,lineIndex)=> {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            currentPage++;
            addHeader('Personalized Recommendations',currentPage);
            yPosition=42;
          }
          doc.text(line,margin + 5,yPosition);
          yPosition +=3.5;
        });
        yPosition +=1;
      });

      // Calendar Schedule section
      doc.addPage();
      currentPage++;
      addHeader('Calendar Schedule',currentPage);
      yPosition=42;

      doc.setTextColor(0,0,0);
      doc.setFontSize(9);
      doc.setFont('helvetica','bold');
      doc.text('Monthly Calendar',pageWidth / 2,35,{align: 'center'});

      yPosition=45;

      const monthlyCalendar=formData.scheduledTasks?.monthly || {};
      const weeks=['Week 1','Week 2','Week 3','Week 4'];

      const calendarStartX=margin;
      const calendarStartY=yPosition;
      const calendarWidth=contentWidth;
      const calendarHeight=pageHeight - yPosition - 20;

      const cellWidth=calendarWidth / 4;
      const cellHeight=calendarHeight;

      doc.setDrawColor(200,200,200);
      doc.setLineWidth(0.3);

      for (let i=0;i <=4;i++) {
        doc.line(
          calendarStartX + i * cellWidth,
          calendarStartY,
          calendarStartX + i * cellWidth,
          calendarStartY + calendarHeight
        );
      }

      doc.line(calendarStartX,calendarStartY,calendarStartX + calendarWidth,calendarStartY);
      doc.line(
        calendarStartX,
        calendarStartY + calendarHeight,
        calendarStartX + calendarWidth,
        calendarStartY + calendarHeight
      );

      weeks.forEach((week,weekIndex)=> {
        const weekKey=`week_${weekIndex + 1}`;
        const allWeekTasks=[];
        
        const monthlyWeekTasks=monthlyCalendar[weekKey] || [];
        allWeekTasks.push(...monthlyWeekTasks);
        
        const sixMonthWeekKey=`month_1_week_${weekIndex + 1}`;
        const sixMonthTasks=formData.scheduledTasks?.sixMonth?.[sixMonthWeekKey] || [];
        allWeekTasks.push(...sixMonthTasks);
        
        const yearlyMonth1Tasks=formData.scheduledTasks?.yearly?.['quarter_1']?.filter(task=> 
          task.month===1 && task.week===weekIndex + 1
        ) || [];
        allWeekTasks.push(...yearlyMonth1Tasks);

        doc.setFont('helvetica','bold');
        doc.setFontSize(7);
        doc.setTextColor(255,255,255);
        doc.setFillColor(...colors.blue);
        
        const headerX=calendarStartX + weekIndex * cellWidth;
        const headerY=calendarStartY - 5;
        doc.rect(headerX,headerY,cellWidth,5,'F');
        doc.text(week,headerX + cellWidth / 2,headerY + 3.5,{align: 'center'});

        doc.setTextColor(0,0,0);
        doc.setFont('helvetica','normal');
        doc.setFontSize(5);

        let taskY=calendarStartY + 4;
        
        allWeekTasks.forEach((task,taskIndex)=> {
          if (taskY + 6 < calendarStartY + calendarHeight - 3) {
            const isCompleted=isTaskCompleted(
              allGoals.findIndex(g=> g.text===task.goalText),
              taskIndex,
              task.timeframe,
              task.week,
              task.month
            );
            
            if (isCompleted) {
              doc.line(headerX + 2,taskY + 1,headerX + 4,taskY + 1);
              doc.line(headerX + 2,taskY,headerX + 2,taskY + 2);
              doc.line(headerX + 4,taskY,headerX + 4,taskY + 2);
            } else {
              doc.rect(headerX + 2,taskY,2,2);
            }
            
            const taskText=task.text;
            const maxTextWidth=cellWidth - 8;
            const lines=wrapText(doc,taskText,maxTextWidth,5,1.1);
            
            lines.forEach((line,lineIndex)=> {
              if (taskY + lineIndex * 6 < calendarStartY + calendarHeight - 3) {
                doc.text(line,headerX + 5,taskY + lineIndex * 6 + 1.5);
              }
            });
            
            taskY +=lines.length * 6 + 1.5;
          }
        });
      });

      doc.addPage();
      currentPage++;
      addHeader('6-Month Calendar',currentPage);
      yPosition=42;

      const sixMonthCalendar=formData.scheduledTasks?.sixMonth || {};
      const gridStartX=margin;
      const gridStartY=yPosition;
      const gridWidth=contentWidth;
      const gridHeight=pageHeight - yPosition - 20;

      const gridCellWidth=gridWidth / 4;
      const gridCellHeight=gridHeight / 6;

      doc.setDrawColor(200,200,200);
      doc.setLineWidth(0.3);

      for (let i=0;i <=4;i++) {
        doc.line(
          gridStartX + i * gridCellWidth,
          gridStartY,
          gridStartX + i * gridCellWidth,
          gridStartY + gridHeight
        );
      }

      for (let i=0;i <=6;i++) {
        doc.line(
          gridStartX,
          gridStartY + i * gridCellHeight,
          gridStartX + gridWidth,
          gridStartY + i * gridCellHeight
        );
      }

      const months=['Month 1','Month 2','Month 3','Month 4','Month 5','Month 6'];
      
      doc.setFont('helvetica','bold');
      doc.setFontSize(6);
      doc.setTextColor(255,255,255);
      doc.setFillColor(...colors.purple);
      
      months.forEach((month,monthIndex)=> {
        const monthY=gridStartY + monthIndex * gridCellHeight;
        doc.rect(gridStartX - 10,monthY,10,gridCellHeight,'F');
        doc.save();
        doc.translate(gridStartX - 5,monthY + gridCellHeight / 2);
        doc.rotate(-90);
        doc.text(month,0,0,{align: 'center'});
        doc.restore();
      });

      const weekHeaders=['Week 1','Week 2','Week 3','Week 4'];
      weekHeaders.forEach((week,weekIndex)=> {
        const weekX=gridStartX + weekIndex * gridCellWidth;
        doc.rect(weekX,gridStartY - 6,gridCellWidth,6,'F');
        doc.setTextColor(255,255,255);
        doc.text(week,weekX + gridCellWidth / 2,gridStartY - 2,{align: 'center'});
      });

      doc.setTextColor(0,0,0);
      doc.setFont('helvetica','normal');
      doc.setFontSize(4);

      for (let month=1;month <=6;month++) {
        for (let week=1;week <=4;week++) {
          const weekKey=`month_${month}_week_${week}`;
          const tasks=sixMonthCalendar[weekKey] || [];
          
          const monthlyTasksForWeek=formData.scheduledTasks?.monthly?.[`week_${week}`] || [];
          
          const yearlyTasksForWeek=formData.scheduledTasks?.yearly?.[`quarter_${Math.ceil(month / 3)}`]?.filter(
            task=> task.month===month && task.week===week
          ) || [];
          
          const allTasks=[...tasks,...monthlyTasksForWeek,...yearlyTasksForWeek];
          
          if (allTasks.length > 0) {
            const cellX=gridStartX + (week - 1) * gridCellWidth;
            const cellY=gridStartY + (month - 1) * gridCellHeight;
            
            let taskY=cellY + 3;
            
            allTasks.forEach((task,taskIndex)=> {
              if (taskY + 4 < cellY + gridCellHeight - 2) {
                const isCompleted=isTaskCompleted(
                  allGoals.findIndex(g=> g.text===task.goalText),
                  taskIndex,
                  task.timeframe,
                  task.week,
                  task.month
                );
                
                if (isCompleted) {
                  doc.line(cellX + 1,taskY + 0.6,cellX + 2.2,taskY + 0.6);
                  doc.line(cellX + 1,taskY,cellX + 1,taskY + 1.2);
                  doc.line(cellX + 2.2,taskY,cellX + 2.2,taskY + 1.2);
                } else {
                  doc.rect(cellX + 1,taskY,1.2,1.2);
                }
                
                const taskText=task.text.length > 12 ? task.text.substring(0,12) + '...' : task.text;
                doc.text(taskText,cellX + 3,taskY + 1);
                
                taskY +=4;
              }
            });
          }
        }
      }

      doc.addPage();
      currentPage++;
      addHeader('Yearly Calendar',currentPage);
      yPosition=42;

      const yearlyCalendar=formData.scheduledTasks?.yearly || {};
      const quarters=['Q1','Q2','Q3','Q4'];

      const quarterlyStartX=margin;
      const quarterlyStartY=yPosition;
      const quarterlyWidth=contentWidth;
      const quarterlyHeight=pageHeight - yPosition - 20;

      const quarterCellWidth=quarterlyWidth / 4;
      const quarterCellHeight=quarterlyHeight;

      doc.setDrawColor(200,200,200);
      doc.setLineWidth(0.3);

      for (let i=0;i <=4;i++) {
        doc.line(
          quarterlyStartX + i * quarterCellWidth,
          quarterlyStartY,
          quarterlyStartX + i * quarterCellWidth,
          quarterlyStartY + quarterlyHeight
        );
      }

      doc.line(quarterlyStartX,quarterlyStartY,quarterlyStartX + quarterlyWidth,quarterlyStartY);
      doc.line(
        quarterlyStartX,
        quarterlyStartY + quarterlyHeight,
        quarterlyStartX + quarterlyWidth,
        quarterlyStartY + quarterlyHeight
      );

      doc.setFont('helvetica','bold');
      doc.setFontSize(7);
      doc.setTextColor(255,255,255);
      doc.setFillColor(...colors.green);
      
      quarters.forEach((quarter,quarterIndex)=> {
        const quarterX=quarterlyStartX + quarterIndex * quarterCellWidth;
        doc.rect(quarterX,quarterlyStartY - 6,quarterCellWidth,6,'F');
        doc.text(quarter,quarterX + quarterCellWidth / 2,quarterlyStartY - 2,{align: 'center'});
      });

      doc.setTextColor(0,0,0);
      doc.setFont('helvetica','normal');
      doc.setFontSize(5);

      for (let quarter=1;quarter <=4;quarter++) {
        const quarterKey=`quarter_${quarter}`;
        const quarterYearlyTasks=yearlyCalendar[quarterKey] || [];
        
        const quarterMonthlyTasks=[];
        const quarterMonths=[quarter * 3 - 2,quarter * 3 - 1,quarter * 3];
        quarterMonths.forEach(month=> {
          if (month <=4) {
            const monthlyWeekTasks=formData.scheduledTasks?.monthly?.[`week_${month}`] || [];
            quarterMonthlyTasks.push(...monthlyWeekTasks);
          }
        });
        
        const quarterSixMonthTasks=[];
        const quarterSixMonthMonths=quarterMonths.filter(m=> m <=6);
        quarterSixMonthMonths.forEach(month=> {
          for (let week=1;week <=4;week++) {
            const weekKey=`month_${month}_week_${week}`;
            const sixMonthTasks=formData.scheduledTasks?.sixMonth?.[weekKey] || [];
            quarterSixMonthTasks.push(...sixMonthTasks);
          }
        });
        
        const allQuarterTasks=[...quarterYearlyTasks,...quarterMonthlyTasks,...quarterSixMonthTasks];
        
        if (allQuarterTasks.length > 0) {
          const quarterX=quarterlyStartX + (quarter - 1) * quarterCellWidth;
          let taskY=quarterlyStartY + 5;
          
          allQuarterTasks.forEach((task,taskIndex)=> {
            if (taskY + 6 < quarterlyStartY + quarterlyHeight - 3) {
              const isCompleted=isTaskCompleted(
                allGoals.findIndex(g=> g.text===task.goalText),
                taskIndex,
                task.timeframe,
                task.week,
                task.month
              );
              
              if (isCompleted) {
                doc.line(quarterX + 2,taskY + 0.75,quarterX + 3.5,taskY + 0.75);
                doc.line(quarterX + 2,taskY,quarterX + 2,taskY + 1.5);
                doc.line(quarterX + 3.5,taskY,quarterX + 3.5,taskY + 1.5);
              } else {
                doc.rect(quarterX + 2,taskY,1.5,1.5);
              }
              
              const maxTextWidth=quarterCellWidth - 10;
              const lines=wrapText(doc,task.text,maxTextWidth,5,1.1);
              
              lines.forEach((line,lineIndex)=> {
                if (taskY + lineIndex * 6 < quarterlyStartY + quarterlyHeight - 3) {
                  doc.text(line,quarterX + 5,taskY + lineIndex * 6 + 1.5);
                }
              });
              
              taskY +=lines.length * 6 + 2;
            }
          });
        }
      }

      // Add footers to all pages
      const totalPages=doc.internal.getNumberOfPages();
      for (let i=1;i <=totalPages;i++) {
        doc.setPage(i);
        addFooter(i,totalPages);
      }

      // Save the PDF
      doc.save(`${formData.name?.replace(/\s+/g,'_') || 'Marketing_Plan'}_Calendar.pdf`);
    } catch (error) {
      console.error('Error generating PDF:',error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleStartAgain=()=> {
    updateFormData({
      name: '',
      subject: '',
      experience: '',
      currentStudents: '',
      teachingMethod: '',
      targetAudience: '',
      niche: '',
      uniqueValue: '',
      targetMarket: '',
      monthlyGoals: [],
      sixMonthGoals: [],
      yearlyGoals: [],
      actionPlan: {},
      scheduledTasks: {}
    });
    setCurrentStep(0);
    window.location.hash='/';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{opacity: 0,y: 30}}
        animate={{opacity: 1,y: 0}}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-500 to-blue-600 p-8 text-white text-center">
          <motion.div
            initial={{scale: 0}}
            animate={{scale: 1}}
            transition={{delay: 0.2}}
            className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-4"
          >
            <SafeIcon icon={FiCheckCircle} className="text-3xl" />
          </motion.div>
          <h2 className="text-3xl font-bold mb-2">Congratulations,{formData.name}!</h2>
          <p className="text-lg opacity-90">Your personalized marketing plan is ready</p>
        </div>

        <div className="p-8">
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-2xl text-blue-600 mx-auto mb-2" />
              <div className="font-semibold text-gray-800">Monthly Goals</div>
              <div className="text-2xl font-bold text-blue-600">{formData.monthlyGoals?.length || 0}</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-2xl text-purple-600 mx-auto mb-2" />
              <div className="font-semibold text-gray-800">6-Month Goals</div>
              <div className="text-2xl font-bold text-purple-600">{formData.sixMonthGoals?.length || 0}</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <SafeIcon icon={FiTarget} className="text-2xl text-green-600 mx-auto mb-2" />
              <div className="font-semibold text-gray-800">Yearly Goals</div>
              <div className="text-2xl font-bold text-green-600">{formData.yearlyGoals?.length || 0}</div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">📅 Your Calendar Schedule</h3>
            <div className="flex justify-center mb-6">
              <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                <button
                  onClick={()=> setActiveCalendarView('monthly')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    activeCalendarView==='monthly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={()=> setActiveCalendarView('6month')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    activeCalendarView==='6month'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  6-Month
                </button>
                <button
                  onClick={()=> setActiveCalendarView('yearly')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    activeCalendarView==='yearly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>
            {renderCalendarSchedule()}
          </div>

          <div className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Your Teaching Niche</h3>
            <p className="text-lg text-gray-700 mb-4">{formData.niche}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Unique Value Proposition:</h4>
                <p className="text-base text-gray-600">{formData.uniqueValue}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Target Market:</h4>
                <p className="text-base text-gray-600">{formData.targetMarket}</p>
              </div>
            </div>
          </div>

          {/* SWOT Analysis Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <SafeIcon icon={FiBarChart} className="mr-2 text-purple-600" />
              SWOT Analysis
            </h3>
            
            {formData.marketingAnalysis ? (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Strengths */}
                <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiAward} className="text-green-600 text-xl mr-2" />
                    <h4 className="font-bold text-green-800">Strengths</h4>
                  </div>
                  <ul className="space-y-2">
                    {formData.marketingAnalysis.strengths?.map((strength,index)=> (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-green-700">{strength}</span>
                      </li>
                    )) || <li className="text-green-700">No strengths identified</li>}
                  </ul>
                </div>
                
                {/* Weaknesses */}
                <div className="bg-red-50 rounded-lg p-5 border border-red-200">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiTarget} className="text-red-600 text-xl mr-2" />
                    <h4 className="font-bold text-red-800">Weaknesses</h4>
                  </div>
                  <ul className="space-y-2">
                    {formData.marketingAnalysis.weaknesses?.map((weakness,index)=> (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiTarget} className="text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-red-700">{weakness}</span>
                      </li>
                    )) || <li className="text-red-700">No weaknesses identified</li>}
                  </ul>
                </div>
                
                {/* Opportunities */}
                <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiCompass} className="text-blue-600 text-xl mr-2" />
                    <h4 className="font-bold text-blue-800">Opportunities</h4>
                  </div>
                  <ul className="space-y-2">
                    {formData.marketingAnalysis.opportunities?.map((opportunity,index)=> (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiCompass} className="text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-blue-700">{opportunity}</span>
                      </li>
                    )) || <li className="text-blue-700">No opportunities identified</li>}
                  </ul>
                </div>
                
                {/* Threats */}
                <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiUsers} className="text-orange-600 text-xl mr-2" />
                    <h4 className="font-bold text-orange-800">Threats</h4>
                  </div>
                  <ul className="space-y-2">
                    {formData.marketingAnalysis.threats?.map((threat,index)=> (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiUsers} className="text-orange-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-orange-700">{threat}</span>
                      </li>
                    )) || <li className="text-orange-700">No threats identified</li>}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-6 text-center">
                <p className="text-gray-600">No SWOT analysis available</p>
              </div>
            )}
          </div>

          {/* Recommendations Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <SafeIcon icon={FiStar} className="mr-2 text-yellow-500" />
              Personalized Recommendations
            </h3>
            
            {formData.marketingAnalysis?.recommendations?.length > 0 ? (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
                <ul className="space-y-4">
                  {formData.marketingAnalysis.recommendations.map((recommendation,index)=> (
                    <li key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                      <SafeIcon icon={FiStar} className="text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-6 text-center">
                <p className="text-gray-600">No recommendations available</p>
              </div>
            )}
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Your Goals & Action Plan</h3>
            {allGoals.map((goal,index)=> {
              const goalKey=`goal_${index}`;
              const tasks=formData.actionPlan?.[goalKey] || [];
              return (
                <motion.div
                  key={index}
                  initial={{opacity: 0,y: 20}}
                  animate={{opacity: 1,y: 0}}
                  transition={{delay: index * 0.1}}
                  className="mb-6 border border-gray-200 rounded-lg p-6"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                      goal.timeframe==='Monthly' ? 'bg-blue-100 text-blue-800' :
                      goal.timeframe==='6-Month' ? 'bg-purple-100 text-purple-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {goal.timeframe}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-lg">{goal.text}</h4>
                  <div className="ml-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Action Tasks:</h5>
                    <ul className="space-y-1">
                      {tasks.map((task,taskIndex)=> (
                        <li key={taskIndex} className="flex items-center space-x-2 text-base text-gray-600">
                          <input
                            type="checkbox"
                            checked={isTaskCompleted(index,taskIndex,goal.timeframe,task.week,task.month)}
                            onChange={()=> toggleTaskCompletion(index,taskIndex,goal.timeframe,task.week,task.month)}
                            className="w-4 h-4 border-2 border-gray-300 rounded cursor-pointer hover:border-blue-500"
                          />
                          <span className={`${
                            isTaskCompleted(index,taskIndex,goal.timeframe,task.week,task.month) 
                              ? 'line-through opacity-60' 
                              : ''
                          }`}>
                            {typeof task==='string' ? task : task.text}
                          </span>
                          {task.week && (
                            <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                              {goal.timeframe==='Monthly' ? `Week ${task.week}` : `M${task.month}W${task.week}`}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg"
            >
              <SafeIcon icon={FiDownload} />
              <span>{isGeneratingPDF ? 'Generating...' : 'Download Your Plan & Calendar (PDF)'}</span>
            </motion.button>
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={handleStartAgain}
              className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-all shadow-lg"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Start Again</span>
            </motion.button>
          </div>

          <div className="bg-yellow-50 rounded-lg p-6">
            <h3 className="font-semibold text-yellow-800 mb-4">🎯 Next Steps to Success</h3>
            <ul className="text-sm text-yellow-700 space-y-2">
              <li>
                1. <strong>Follow your calendar schedule</strong> - Stick to the weekly and monthly plans you've created
              </li>
              <li>2. <strong>Start with Week 1 tasks</strong> - Focus on completing the first week's activities</li>
              <li>3. <strong>Track your progress</strong> - Mark completed tasks and adjust as needed</li>
              <li>4. <strong>Review monthly</strong> - At the end of each month, assess your progress and plan ahead</li>
              <li>5. <strong>Stay consistent</strong> - Marketing success comes from consistent daily actions</li>
            </ul>
          </div>
        </div>
      </motion.div>
      
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{delay: 0.5}}
        className="text-center mt-8 p-6 bg-green-50 rounded-lg"
      >
        <h3 className="text-lg font-semibold text-green-800 mb-2">
          🎉 You're Ready to Grow Your Teaching Business!
        </h3>
        <p className="text-green-700">
          Remember, successful marketing is about consistency and providing value to your students. 
          Start implementing your plan today and watch your teaching business flourish!
        </p>
      </motion.div>
    </div>
  );
};

export default CompletePlan;